import RichTextEditor from "../TextEditor";
import CheckIcon from "@mui/icons-material/Check";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  ListItemIcon,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CURRENCY, PACKAGE_USAGE_TYPE } from "../../constants/Variables";
import globalStyle from "../../globalStyle";
import { getAllEcommerceTags } from "../../services/ecommerceTageApiFn";
import {
  fetchCustomPackageDetail,
  updateCustomPackageByUrl,
} from "../../services/userPackageFn";
import { decodeHtml, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import {
  addEditUserPackageValidation
} from "../../utils/Validation";
import AppLoader from "../AppLoader";
import PaperContainer from "../Containers/PaperContainer";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import Label from "../Label";
import TextInput from "../TextInputs";
import ProductImages from "./ProductImages";

const initialInputValues = {
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  specification_en: "",
  specification_ro: "",
  tags: [],
};

const initialLoading = {
  translateToEnglish: false,
  save: false,
  verify: false,
  fetch: false,
};

const AddEditUserPackage = ({ handleChangeTab }) => {
  const errorRef = useRef(null);
  const { t } = useTranslation(["products", "common"]);
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [detailData, setDetailData] = useState(null);
  const [allEcommerceTags, setAllEcommerceTags] = useState([]);
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);

  const { currentLanguage } = useSelector((state) => state.languageReducer);
  const userPackageId = useSelector(
    (state) => state.detailIdReducer.detailIds.userPackageId
  );

  const fetchchedAllEcommerceTags = async (solutionType) => {
    try {
      const params = {
        solutionType: solutionType,
        appliesTo: "package",
      };
      const response = await getAllEcommerceTags(params);
      if (response?.data?.status === "success") {
        const data = response?.data?.data
        const formattedData = data.map((item) => ({
          label: item.name,
          value: item._id,
        }));
        setAllEcommerceTags(formattedData);
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchchedAllEcommerceTags(detailData?.solutionType);
  }, [detailData?.solutionType]);

  const handleFetchCustomPackageDetail = async (packageId) => {
    try {
      setIsLoading((prev) => ({ ...prev, fetch: true }));
      const response = await fetchCustomPackageDetail(packageId);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        setDetailData(data);
        setInputValues({
          name_en: data?.name?.en,
          name_ro: data?.name?.ro,
          description_en: data?.description?.en,
          description_ro: data?.description?.ro,
          specification_en: decodeHtml(data?.specification?.en),
          specification_ro: decodeHtml(data?.specification?.ro),
          tags: data?.tags || [],
        });
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    if (userPackageId) {
      handleFetchCustomPackageDetail(userPackageId);
    }
  }, [userPackageId]);

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditorChangeEnglish = (html) => {
    setInputError((err) => ({ ...err, specification_en: "" }));
    setInputValues((val) => ({ ...val, specification_en: html }));
  };

  const handleEditorChangeRomanian = (html) => {
    setInputError((err) => ({ ...err, specification_ro: "" }));
    setInputValues((val) => ({ ...val, specification_ro: html }));
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.focus();
        }
      }, 300);
    }
  };

  const handleSave = async () => {
    errorRef.current = null;

    const hasError = addEditUserPackageValidation(
      inputValues,
      setInputError,
      errorRef
    );
    if (hasError) {
      scrollToError();
      return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));
    }

    const payload = {
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      specification: {
        en: inputValues.specification_en,
        ro: inputValues.specification_ro,
      },
      tags: inputValues.tags,
    };

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await updateCustomPackageByUrl(
        detailData?.mlSlug?.en,
        payload
      );
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleFetchCustomPackageDetail(detailData?._id);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleVerify = async () => {
    errorRef.current = null;

    const hasError = addEditUserPackageValidation(
      inputValues,
      setInputError,
      errorRef
    );
    if (hasError) {
      scrollToError();
      return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));
    }

    try {
      setIsLoading((prev) => ({ ...prev, verify: true }));
      const response = await updateCustomPackageByUrl(detailData?.mlSlug?.en, {
        isVerified: !detailData?.isVerified,
        status: !detailData?.status,
      });
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleFetchCustomPackageDetail(detailData?._id);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, verify: false }));
    }
  };

  const handleDiscard = () => {
    handleChangeTab("custom_packages_list");
  };

  const handleDeleteTag = (tag) => {
    setInputValues((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
    setInputError((prev) => ({ ...prev, tags: "" }));
  };

  return (
    <>
      <AppLoader isVisible={isLoading.fetch || isLoading.save || isLoading.verify} />
      <PaperContainer
        title={t("customPackageDetails")}
        subtitle={t("customPackageDetailsInstruction")}
        showBackButton={true}
        onBack={() => {
          handleChangeTab("custom_packages_list");
        }}
      >
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Typography fontSize={20} lineHeight={"normal"} fontWeight={600}>
              {t("images")}
            </Typography>
          </Grid>

          <Grid item xs={12} mt={2}>
            <ProductImages inputValues={detailData} showAddImageBox={false} />
          </Grid>

          <Grid item xs={12}>
            <Label
              labelText={t("packageNameInRomanian")}
              required
              htmlFor={`user-package-name-ro`}
            />
            <TextInput
              id={`user-package-name-ro`}
              styleProps={globalStyle.textField}
              placeholder={t("enterPackageNameInRomanian")}
              required
              name={`name_ro`}
              value={inputValues.name_ro}
              onChange={handleChangeValues}
              inputError={Boolean(inputError.name_ro)}
              helperText={t(inputError.name_ro)}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("packageNameInEnglish")}
                required
                htmlFor={`user-package-name-en`}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish("name_ro", "name_en")
                }
                icon={
                  isLoading.translateToEnglish ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translateToEnglish}
              />
            </Stack>
            <TextInput
              id={`user-package-name-en`}
              styleProps={globalStyle.textField}
              placeholder={t("enterPackageNameInEnglish")}
              required
              name={`name_en`}
              value={inputValues.name_en}
              onChange={handleChangeValues}
              inputError={Boolean(inputError.name_en)}
              helperText={t(inputError.name_en)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("solutionType")}
              htmlFor={`user-package-solution-type`}
            />
            <TextInput
              id={`user-package-solution-type`}
              styleProps={globalStyle.textField}
              value={PACKAGE_USAGE_TYPE.find(item => detailData?.solutionType?.includes(item.value))?.label[currentLanguage]}
              readOnly
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("category")}
              htmlFor={`user-package-category`}
            />
            <TextInput
              id={`user-package-category`}
              styleProps={globalStyle.textField}
              value={detailData?.categoryId?.name[currentLanguage]}
              readOnly
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("subCategory")}
              htmlFor={`user-package-sub-category`}
            />
            <TextInput
              id={`user-package-sub-category`}
              styleProps={globalStyle.textField}
              value={detailData?.subCategoryId
                ?.map((item) => item?.name[currentLanguage])
                .join(", ")}
              readOnly
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("subSubCategory")}
              htmlFor={`user-package-sub-sub-category`}
            />
            <TextInput
              id={`user-package-sub-sub-category`}
              styleProps={globalStyle.textField}
              value={detailData?.subSubCategoryId
                ?.map((item) => item?.name[currentLanguage])
                .join(", ")}
              readOnly
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("finalPrice")}
              htmlFor={"user-package-final-price"}
            />
            <TextInput
              id={"user-package-final-price"}
              styleProps={globalStyle.textField}
              readonly
              value={detailData?.effectivePackagePrice}
              InputEndIcon={CURRENCY}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label
              labelText={t("totalOrders")}
              htmlFor={"user-package-total-order"}
            />
            <TextInput
              id={"user-package-total-order"}
              styleProps={globalStyle.textField}
              readonly
              value={detailData?.totalOrders}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Label labelText={t("createdBy")} htmlFor={"user-package-created-by"} />
            <TextInput
              id={"user-package-created-by"}
              styleProps={globalStyle.textField}
              readonly
              value={
                detailData?.createdBy?.firstname +
                " " +
                detailData?.createdBy?.lastname
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={t("chooseAMatchFit")}
              required
              htmlFor={"user-package-tags"}
            />
            <CustomDropDown
              id={"user-package-tags"}
              name="tags"
              value={inputValues.tags}
              onChange={handleChangeValues}
              inputError={Boolean(inputError.tags)}
              errorText={inputError.tags ? t(inputError.tags) : ""}
              multiple
              renderValue={(selected) =>
                selected.map((item) => {
                  const found = allEcommerceTags.find((tag) => tag.value === item);
                  if (!found) return null;
                  return (
                    <Chip key={item.value} label={found?.label?.[currentLanguage]}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        marginRight: 0.5,
                        marginBottom: 0.5,
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleDeleteTag(item);
                      }}
                    />
                  )
                })
              }
            >
              <MenuItem value="" disabled>
                {t("selectMultipleMatchFit")}
              </MenuItem>
              {allEcommerceTags.map((item, index) => {
                const isSelected = inputValues.tags.includes(item.value);

                return (
                  <MenuItem key={index} value={item.value} sx={{ textTransform: "capitalize", paddingX: "8px" }} >
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      {isSelected && <CheckIcon fontSize="small" htmlColor="#7922D8" />}
                    </ListItemIcon>
                    {item.label[currentLanguage]}
                  </MenuItem>
                )
              })}
            </CustomDropDown>
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={t("descriptionInRomanian")}
              required
              htmlFor={"user-package-description-ro"}
            />
            <TextInput
              id={"user-package-description-ro"}
              styleProps={globalStyle.textField}
              placeholder={t("enterDescriptionInRomanian")}
              multiline
              rows={4}
              name={`description_ro`}
              value={inputValues.description_ro}
              onChange={handleChangeValues}
              inputError={Boolean(inputError.description_ro)}
              helperText={t(inputError.description_ro)}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("descriptionInEnglish")}
                required
                htmlFor={"user-package-description-en"}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish("description_ro", "description_en")
                }
                icon={
                  isLoading.translateToEnglish ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translateToEnglish}
              />
            </Stack>
            <TextInput
              id={"user-package-description-en"}
              styleProps={globalStyle.textField}
              placeholder={t("enterDescriptionInEnglish")}
              multiline
              rows={4}
              name={`description_en`}
              value={inputValues.description_en}
              onChange={handleChangeValues}
              inputError={Boolean(inputError.description_en)}
              helperText={t(inputError.description_en)}
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              required
              labelText={t("specificationInEnglish")}
              htmlFor={"user-package-specifications-en"}
            />

            <RichTextEditor
              value={inputValues.specification_en || ""}
              onChange={handleEditorChangeEnglish}
            />

            {inputError.specification_en && (
              <FormHelperText error>
                {t(inputError.specification_en)}
              </FormHelperText>
            )}

          </Grid>
          <Grid item xs={12}>
            <Label
              required
              labelText={t("specificationInRomanian")}
              htmlFor={"user-package-specifications-ro"}
            />

            <RichTextEditor
              value={inputValues.specification_ro || ""}
              onChange={handleEditorChangeRomanian}
            />

            {inputError.specification_ro && (
              <FormHelperText error>
                {t(inputError.specification_ro)}
              </FormHelperText>
            )}

          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
          <GlobalButton
            btnText={t("save")}
            handleClickBtn={handleSave}
            styleProps={{ width: 100, height: 40 }}
          />
          <GlobalButton
            btnText={detailData?.isVerified ? t("unverify") : t("verify")}
            handleClickBtn={handleVerify}
            styleProps={{ width: 100, height: 40 }}
          />
          <GlobalButton
            btnText={t("discard")}
            handleClickBtn={handleDiscard}
            styleProps={{ width: 100, height: 40 }}
          />
        </Stack>
      </PaperContainer >
    </>
  );
};

export default AddEditUserPackage;
